if (!customElements.get("neighbor-sticky-cart")) {
  customElements.define(
    "neighbor-sticky-cart",
    class NeighborStickyCart extends HTMLElement {
      constructor() {
        super();

        this.activeOffer = this.dataset.defaultOffer || "max";
        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        this.offers = Array.from(
          this.querySelectorAll("[data-neighbor-sticky-offer]"),
        );
        this.showAfterScroll = Number(this.dataset.showAfterScroll || 0);
        this.solarSection = document.querySelector(
          this.dataset.solarSectionSelector,
        );

        this.bindVariantSelectors();
        this.cacheSolarSwitchPoint();
        this.setActiveOffer(this.activeOffer);
        this.handleScroll();

        window.addEventListener("scroll", this.handleScroll, {
          passive: true,
        });
        window.addEventListener("resize", this.handleResize);
        document.addEventListener("click", this.handleDocumentClick);
        document.addEventListener("keydown", this.handleKeydown);
      }

      disconnectedCallback() {
        window.removeEventListener("scroll", this.handleScroll);
        window.removeEventListener("resize", this.handleResize);
        document.removeEventListener("click", this.handleDocumentClick);
        document.removeEventListener("keydown", this.handleKeydown);

        document.body.classList.remove("neighbor-sticky-cart-visible");
        document.body.classList.remove("neighbor-sticky-cart-picker-open");
      }

      bindVariantSelectors() {
        this.offers.forEach((offer) => {
          const select = offer.querySelector("[data-neighbor-sticky-select]");
          const variantInput = offer.querySelector(
            "[data-neighbor-sticky-variant-id]",
          );
          const price = offer.querySelector("[data-neighbor-sticky-price]");
          const image = offer.querySelector("[data-neighbor-sticky-image]");
          const mobilePicker = offer.querySelector(
            "[data-neighbor-sticky-mobile-picker]",
          );
          const mobileTrigger = offer.querySelector(
            "[data-neighbor-sticky-mobile-trigger]",
          );
          const mobileLabel = offer.querySelector(
            "[data-neighbor-sticky-mobile-label]",
          );
          const mobileOptions = Array.from(
            offer.querySelectorAll("[data-neighbor-sticky-mobile-option]"),
          );

          if (!select || !variantInput) return;

          select.addEventListener("change", () => {
            const selectedOption = select.selectedOptions[0];
            this.updateOfferVariant({
              offer,
              select,
              variantInput,
              price,
              image,
              mobileLabel,
              mobileOptions,
              value: select.value,
              label: selectedOption?.textContent?.trim(),
              priceText: selectedOption?.dataset.price,
              imageSrc: selectedOption?.dataset.imageSrc,
              imageAlt: selectedOption?.dataset.imageAlt,
            });
          });

          if (mobileTrigger && mobilePicker) {
            mobileTrigger.addEventListener("click", (event) => {
              event.stopPropagation();
              this.toggleMobilePicker(mobilePicker);
            });
          }

          mobileOptions.forEach((option) => {
            option.addEventListener("click", () => {
              if (option.disabled) return;

              select.value = option.dataset.value;
              this.updateOfferVariant({
                offer,
                select,
                variantInput,
                price,
                image,
                mobileLabel,
                mobileOptions,
                value: option.dataset.value,
                label: option.dataset.label,
                priceText: option.dataset.price,
                imageSrc: option.dataset.imageSrc,
                imageAlt: option.dataset.imageAlt,
              });
              this.closeMobilePickers();
            });
          });
        });
      }

      updateOfferVariant({
        variantInput,
        price,
        image,
        mobileLabel,
        mobileOptions,
        value,
        label,
        priceText,
        imageSrc,
        imageAlt,
      }) {
        variantInput.value = value;

        if (price && priceText) {
          price.textContent = priceText;
        }

        if (image && imageSrc) {
          image.src = imageSrc;
          image.removeAttribute("srcset");

          if (imageAlt) {
            image.alt = imageAlt;
          }
        }

        if (mobileLabel && label) {
          mobileLabel.textContent = label;
        }

        mobileOptions.forEach((option) => {
          option.classList.toggle("is-selected", option.dataset.value === value);
        });
      }

      toggleMobilePicker(picker) {
        const shouldOpen = !picker.classList.contains("is-open");

        this.closeMobilePickers();
        picker.classList.toggle("is-open", shouldOpen);
        document.body.classList.toggle(
          "neighbor-sticky-cart-picker-open",
          shouldOpen,
        );

        const trigger = picker.querySelector(
          "[data-neighbor-sticky-mobile-trigger]",
        );
        if (trigger) trigger.setAttribute("aria-expanded", String(shouldOpen));
      }

      closeMobilePickers() {
        this.querySelectorAll("[data-neighbor-sticky-mobile-picker]").forEach(
          (picker) => {
            picker.classList.remove("is-open");

            const trigger = picker.querySelector(
              "[data-neighbor-sticky-mobile-trigger]",
            );
            if (trigger) trigger.setAttribute("aria-expanded", "false");
          },
        );
        document.body.classList.remove("neighbor-sticky-cart-picker-open");
      }

      handleDocumentClick(event) {
        if (this.contains(event.target)) return;

        this.closeMobilePickers();
      }

      handleKeydown(event) {
        if (event.key !== "Escape") return;

        this.closeMobilePickers();
      }

      cacheSolarSwitchPoint() {
        if (!this.solarSection) return;

        this.solarSwitchPoint =
          this.solarSection.getBoundingClientRect().top + window.scrollY;
      }

      handleResize() {
        this.cacheSolarSwitchPoint();
        this.handleScroll();
      }

      handleScroll() {
        const shouldShow = this.shouldShow();

        this.classList.toggle("is-visible", shouldShow);
        document.body.classList.toggle(
          "neighbor-sticky-cart-visible",
          shouldShow,
        );

        this.updateActiveOffer();
      }

      shouldShow() {
        return window.scrollY >= this.showAfterScroll;
      }

      updateActiveOffer() {
        if (!this.solarSwitchPoint) return;

        const nextOffer =
          window.scrollY >= this.solarSwitchPoint
            ? this.dataset.solarOffer || "solar"
            : this.dataset.defaultOffer || "max";

        this.setActiveOffer(nextOffer);
      }

      setActiveOffer(offerKey) {
        if (!offerKey || this.activeOffer === offerKey) return;

        const hasOffer = this.offers.some(
          (offer) => offer.dataset.neighborStickyOffer === offerKey,
        );
        if (!hasOffer) return;

        this.activeOffer = offerKey;

        this.offers.forEach((offer) => {
          const isActive = offer.dataset.neighborStickyOffer === offerKey;

          offer.classList.toggle("is-active", isActive);
          offer.toggleAttribute("hidden", !isActive);
        });
      }
    },
  );
}
